import produce from 'immer';
import { User, UserManager } from 'oidc-client';
import * as React from 'react';
import { createContext, useEffect, useReducer } from 'react';
import { getUserManager } from '../../Config/ApplicationConfig';
import { routerHistory } from './history';

// Log.logger = console;
// Uncomment to turn on detailed debug information
// Log.level = Log.DEBUG;

export enum AuthenticationStatus {
    Init = 'Init',
    Authenticating = 'Authenticating',
    Authenticated = 'Authenticated',
    Errored = 'Errored',
    LoggedOut = 'LoggedOut'
}

type AuthenticateState = {
    status: AuthenticationStatus;
    error: string;
};

type Actions =
    | { type: 'Authenticate' }
    | { type: 'Authenticated'; user: User }
    | { type: 'ErrorAuthenticating'; error: string };

let authenticated: (user: User) => void;

const silentlyAuthenticateAndFetchUser = async (): Promise<User | null> => {
    console.log('Attempting silent sign in');
    try {
        console.log('Doing silent sign in');
        const silentUser: User = await getUserManager().signinSilent();

        if (!!silentUser) {
            console.log('Silent sign in successful');
            return silentUser;
        }

        console.log('Unable to do silent sign in');
        return null;
    } catch (err) {
        console.error('Silent signing error - Unable to do silent sign in: ', err);
        return null;
    }
};

export async function getAuthToken(): Promise<string> {
    const userManager = getUserManager();

    if (userManager) {
        const user = await userManager.getUser();

        if (user) {
            return `${user.token_type} ${user.access_token}`;
        }
    }

    console.error('User token was requested when no user is stored');
    return '';
}

export async function validateOrRenewUser(): Promise<Oidc.User | null> {
    const managersUser = await getUserManager().getUser();

    if (!managersUser || (!!managersUser && managersUser.expired)) {
        const renewedUser: User | null = await silentlyAuthenticateAndFetchUser();
        if (renewedUser) {
            authenticated(renewedUser);
            return renewedUser;
        }

        return null;
    }
    return managersUser;
}

const authenticationReducer = produce((state: AuthenticateState, actions: Actions) => {
    switch (actions.type) {
        case 'Authenticate':
            state.status = AuthenticationStatus.Authenticating;
            break;
        case 'Authenticated':
            state.status = AuthenticationStatus.Authenticated;
            state.error = '';
            break;
        case 'ErrorAuthenticating':
            state.status = AuthenticationStatus.Errored;
            state.error = actions.error;
            break;
    }
});

function reloadApplicationUrl() {
    const redirectUrl = window.sessionStorage.getItem('redirectUrl');
    if (!!redirectUrl && !!routerHistory && routerHistory.push) {
        routerHistory.push(redirectUrl);
        window.sessionStorage.removeItem('redirectUrl');
        console.log('Reloading url: ', redirectUrl);
    }
}

function saveApplicationUrl() {
    const redirectUrl = window.location.pathname + window.location.search;

    if (window.location.pathname !== '/login') {
        window.sessionStorage.setItem('redirectUrl', redirectUrl);
        console.log('Storing url: ', redirectUrl);
    }
}

export const AuthenticationContext = createContext<
    AuthenticateState & {
        authenticate(): Promise<void>;
    }
>({
    status: AuthenticationStatus.Init,
    error: '',
    authenticate(): Promise<void> {
        throw new Error('No authentication context found');
    }
});

export const ApplicationAuthentication: React.FC<{
    manager: UserManager;
}> = ({ manager, children }) => {
    const userLoggedOut = document.location && document.location.hash.indexOf('logged_out') >= 0;

    const [state, dispatch] = useReducer(authenticationReducer, {
        status: userLoggedOut ? AuthenticationStatus.LoggedOut : AuthenticationStatus.Init,
        error: ''
    });

    const signIn = async () => {
        dispatch({ type: 'Authenticate' });
        console.log('No id_token found in URL, looking for user.');

        try {
            let user: User | null = await manager.getUser();

            if (!!user && !user.expired) {
                console.log('Successful call to get user');
                dispatch({ type: 'Authenticated', user });
                return;
            } else {
                user = await silentlyAuthenticateAndFetchUser();
                console.log('Successfully fetched user silently');
                if (!!user) {
                    reloadApplicationUrl();
                    dispatch({ type: 'Authenticated', user });
                    return;
                }
            }

            if (!user) {
                console.log('No user found, calling sign in');
                saveApplicationUrl();

                await manager.signinRedirect();
            }
        } catch (err) {
            console.error('Error with Authenticate: ', err);
        }
    };

    authenticated = (user: User) => {
        dispatch({ type: 'Authenticated', user });
    };

    const processToken = async () => {
        try {
            let user: User | null = await silentlyAuthenticateAndFetchUser();

            if (!!user) {
                console.log('Successfully fetched user silently');
                reloadApplicationUrl();
                dispatch({ type: 'Authenticated', user });
                return;
            } else {
                user = await manager.signinRedirectCallback();
                if (!!user) {
                    reloadApplicationUrl();
                    dispatch({ type: 'Authenticated', user });
                } else {
                    console.log('No user found: ', user);
                    dispatch({ type: 'ErrorAuthenticating', error: 'No user found' });
                }
            }
        } catch (err) {
            console.error('Error processing token: ', err);
            if (typeof err === 'string') {
                dispatch({ type: 'ErrorAuthenticating', error: err });
            }
        }
    };

    const processError = (error: string) => {
        dispatch({ type: 'ErrorAuthenticating', error });
    };

    const authenticate = async () => {
        if (document && document.location) {
            if (userLoggedOut) {
                return;
            } else if (document.location.hash.indexOf('id_token') >= 0) {
                // Token found, processing.
                processToken();
            } else if (document.location.hash.indexOf('error') >= 0) {
                processError('Authentication Error');
            } else {
                // No Token found, firing off authenticationState.
                signIn();
            }
        }
    };

    useEffect(() => {
        const userManagerEvents = getUserManager().events;

        userManagerEvents.addSilentRenewError((err: Error) => {
            console.log(
                'Silent authenticationState renewal failed. If this continues then the access token may expire.',
                err.message
            );
        });

        userManagerEvents.addAccessTokenExpiring(() => {
            console.log('The access token is expiring.');
        });

        userManagerEvents.addAccessTokenExpired(() => {
            console.log('The access token has expired.');
        });

        userManagerEvents.addUserSignedOut(() => {
            console.log('User has been signed out, possibly in another tab. Logging them out.');
            manager.signoutRedirect();
        });

        userManagerEvents.addUserLoaded((user: User) => {
            console.log('User session has been loaded.');
            authenticated(user);
        });

        userManagerEvents.addUserUnloaded((...args) => {
            console.log('User unloaded - args: ', args);
        });

        userManagerEvents.addUserSessionChanged((...args) => {
            console.log('User Session Changed - args: ', args);
        });

        userManagerEvents.addUserSignedOut(() => {
            console.log('Signed out');
        });
    }, []);

    return (
        <AuthenticationContext.Provider
            value={{
                ...state,
                authenticate
            }}
        >
            {children}
        </AuthenticationContext.Provider>
    );
};
